import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveGroup,
  createGroup,
  demoteMember,
  fetchGroup,
  fetchGroupMembers,
  fetchMyGroups,
  fetchRecoverableGroups,
  groupConversionGaps,
  groupsKeys,
  leaveGroup,
  promoteMember,
  removeMember,
  restoreGroup,
  softDeleteGroup,
  unarchiveGroup,
  updateGroup,
  setGhostEmail,
} from '../api';
import { invalidateGroupsList, invalidateGroupFinancials } from '@/shared/lib/invalidate';
import type { GroupCreate, GroupUpdate } from '@shared/schemas/groups';

export function useMyGroups() {
  return useQuery({
    queryKey: groupsKeys.list(),
    queryFn: fetchMyGroups,
    staleTime: 30_000,
  });
}

export function useGroup(groupId: string) {
  return useQuery({
    queryKey: groupsKeys.byId(groupId),
    queryFn: () => fetchGroup(groupId),
    enabled: Boolean(groupId),
    staleTime: 30_000,
  });
}

/**
 * Cuántos gastos se quedarían sin reconvertir si el grupo pasara a
 * `targetCurrency`. Sólo consulta cuando hay un cambio real de divisa: con la
 * divisa actual la respuesta es siempre 0 y no aporta nada.
 *
 * `staleTime` alto a propósito: depende de las cotizaciones y de los gastos,
 * que no cambian mientras se rellena un formulario.
 */
export function useGroupConversionGaps(groupId: string, targetCurrency: string | null) {
  return useQuery({
    queryKey: [...groupsKeys.byId(groupId), 'conversion-gaps', targetCurrency],
    queryFn: () => groupConversionGaps(groupId, targetCurrency),
    enabled: Boolean(groupId) && Boolean(targetCurrency),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGroupMembers(groupId: string) {
  return useQuery({
    queryKey: groupsKeys.members(groupId),
    queryFn: () => fetchGroupMembers(groupId),
    enabled: Boolean(groupId),
    staleTime: 30_000,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GroupCreate) => createGroup(input),
    onSuccess: () => invalidateGroupsList(qc),
  });
}

export function useUpdateGroup(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GroupUpdate) => updateGroup(groupId, input),
    onSuccess: (data) => {
      qc.setQueryData(groupsKeys.byId(groupId), data);
      invalidateGroupsList(qc);
    },
  });
}

export function useArchiveGroup(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => archiveGroup(groupId),
    onSuccess: () => invalidateGroupsList(qc),
  });
}

export function useUnarchiveGroup(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unarchiveGroup(groupId),
    onSuccess: () => invalidateGroupsList(qc),
  });
}

export function useRecoverableGroups() {
  return useQuery({
    queryKey: [...groupsKeys.all, 'recoverable'],
    queryFn: fetchRecoverableGroups,
    staleTime: 60_000,
  });
}

export function useRestoreGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => restoreGroup(groupId),
    onSuccess: () => invalidateGroupsList(qc),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => softDeleteGroup(groupId),
    onSuccess: () => invalidateGroupsList(qc),
  });
}

export function useLeaveGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => leaveGroup(groupId),
    onSuccess: () => invalidateGroupsList(qc),
  });
}

export function usePromoteMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) => promoteMember(groupId, participantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: groupsKeys.byId(groupId) }),
  });
}

export function useDemoteMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) => demoteMember(groupId, participantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: groupsKeys.byId(groupId) }),
  });
}

export function useRemoveMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { participantId: string; force?: boolean }) =>
      removeMember(groupId, args.participantId, args.force ?? false),
    // Quitar un miembro cambia los balances del grupo → invalidación completa.
    onSuccess: () => invalidateGroupFinancials(qc, groupId),
  });
}

/**
 * Darle correo a un fantasma del grupo para que pueda registrarse.
 *
 * Invalida el grupo entero, no sólo los miembros: la fila deja de ser «sin
 * correo» y la acción tiene que desaparecer de su menú.
 */
export function useSetGhostEmail(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, email }: { participantId: string; email: string }) =>
      setGhostEmail(groupId, participantId, email),
    onSuccess: () => void qc.invalidateQueries({ queryKey: groupsKeys.byId(groupId) }),
  });
}
